import React, { useEffect, useRef, useState } from 'react';
import {
  LuCalendar, LuChevronDown, LuChevronLeft, LuChevronRight, LuInfo,
} from 'react-icons/lu';

import { Calendar } from 'primereact/calendar';
import { InputMask } from 'primereact/inputmask';
import { OverlayPanel } from 'primereact/overlaypanel';
import { SelectButton } from 'primereact/selectbutton';
import { Tooltip } from 'primereact/tooltip';

import {
  applyRangeEdit,
  DateRangeValue,
  formatDayBR,
  formatMonthBR,
  parseDayBR,
  parseMonthBR,
} from '../../utils/dateBR';

export type DateMode = 'data' | 'periodo';

export interface DateFilterValue extends DateRangeValue {
  mode: DateMode;
}

interface DateFilterProps {
  id: string;
  label: string;
  value: DateFilterValue;
  onChange: (next: DateFilterValue) => void;
  withMode: boolean;
  // eslint-disable-next-line react/require-default-props
  bare?: boolean;
}

type Side = 'start' | 'end';

const modeOptions = [
  { name: 'Data', value: 'data' },
  { name: 'Período', value: 'periodo' },
];

const shortMonths = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

type Pane = 'days' | 'months' | 'years';

const monthNames = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const firstYear = 1980;
const lastYear = 2030;

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const DateFilter: React.FC<DateFilterProps> = ({
  id, label, value, onChange, withMode, bare = false,
}) => {
  const panel = useRef<OverlayPanel>(null);
  const [active, setActive] = useState<Side>('start');
  const [open, setOpen] = useState(false);
  const [pane, setPane] = useState<Pane>('days');
  const [decade, setDecade] = useState(2020);
  const [viewDate, setViewDate] = useState<Date>(() => startOfMonth(new Date()));

  const isPeriod = value.mode === 'periodo';
  const format = isPeriod ? formatMonthBR : formatDayBR;
  const parse = isPeriod ? parseMonthBR : parseDayBR;

  const unit = isPeriod ? 'Mês' : 'Data';
  const stepLabels = { start: `${unit} inicial`, end: `${unit} final` };

  const [texts, setTexts] = useState({ start: format(value.start), end: format(value.end) });

  useEffect(() => {
    setTexts({ start: format(value.start), end: format(value.end) });
  }, [value.start, value.end, value.mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (side: Side, date: Date | null) => {
    onChange({ ...value, ...applyRangeEdit(value, side, date) });
  };

  const handleBlur = (side: Side) => {
    const text = texts[side];
    if (!/\d/.test(text)) {
      if (value[side]) commit(side, null);
      else setTexts((current) => ({ ...current, [side]: '' }));
      return;
    }
    setTexts((current) => ({ ...current, [side]: format(value[side]) }));
  };

  const openPanel = (event: React.MouseEvent<HTMLElement>, side: Side) => {
    setActive(side);
    panel.current?.toggle(event, event.currentTarget.closest('.date-input'));
  };

  const pickDay = (day: Date) => {
    commit(active, day);
    if (active === 'start') {
      setActive('end');
    } else {
      panel.current?.hide();
    }
  };

  const renderInput = (side: Side, placeholder: string) => (
    <div className={`date-input ${open && active === side ? 'is-active' : ''}`}>
      <InputMask
        id={`${id}_${side}`}
        mask={isPeriod ? '99/9999' : '99/99/9999'}
        slotChar="_"
        autoClear={false}
        placeholder={placeholder}
        value={texts[side]}
        onChange={(e) => setTexts((current) => ({ ...current, [side]: e.value ?? '' }))}
        onComplete={(e) => {
          const parsed = parse(e.value ?? '');
          if (parsed) commit(side, parsed);
        }}
        onBlur={() => handleBlur(side)}
      />
      <button
        type="button"
        className="date-trigger"
        aria-label={`Escolher ${side === 'start' ? 'a data inicial' : 'a data final'} de ${label}`}
        onClick={(event) => openPanel(event, side)}
      >
        <LuCalendar />
      </button>
    </div>
  );

  return (
    <div className="date-filter">
      {bare ? null : (
      <div className="date-head">
        <span className="date-label">
          {label}
          {withMode ? (
            <>
              <LuInfo className={`date-info info-${id}`} />
              <Tooltip target={`.info-${id}`} position="bottom">
                <strong>Selecione data específica ou período</strong>
                <br />
                <strong>Data:</strong>
                {' '}
                <em>intervalos com datas exatas.</em>
                <br />
                <strong>Período:</strong>
                {' '}
                <em>mês inicial e final. Ao escolher o mês inicial, o final já vem igual (vale o mês inteiro).</em>
              </Tooltip>
            </>
          ) : null}
        </span>
        {withMode ? (
          <SelectButton
            className="date-mode"
            value={value.mode}
            options={modeOptions}
            optionLabel="name"
            unselectable={false}
            onChange={(e) => {
              if (e.value) onChange({ mode: e.value, start: null, end: null });
            }}
          />
        ) : null}
      </div>
      )}

      <div className="date-inputs">
        {renderInput('start', isPeriod ? 'Mês inicial' : 'De')}
        <span className="date-to">até</span>
        {renderInput('end', isPeriod ? 'Mês final' : 'Até')}
      </div>

      <OverlayPanel
        ref={panel}
        className="date-panel"
        appendTo={document.body}
        onShow={() => {
          setOpen(true);
          setPane('days');
          setViewDate(startOfMonth(value[active] ?? value.start ?? value.end ?? new Date()));
        }}
        onHide={() => setOpen(false)}
      >
        <div className="date-steps" role="tablist" aria-label={`Datas de ${label}`}>
          {(['start', 'end'] as Side[]).map((side, index) => (
            <button
              key={side}
              type="button"
              role="tab"
              aria-selected={active === side}
              className={`date-step ${active === side ? 'is-current' : ''} ${value[side] ? 'is-filled' : ''}`}
              onClick={() => setActive(side)}
            >
              <span className="date-step-number">{index + 1}</span>
              <span className="date-step-text">
                <strong>{stepLabels[side]}</strong>
                <small>{format(value[side]) || 'não escolhida'}</small>
              </span>
            </button>
          ))}
        </div>
        <p className="date-panel-hint">
          {active === 'start'
            ? `Clique no ${isPeriod ? 'mês' : 'dia'} em que o período começa`
            : `Agora clique no ${isPeriod ? 'mês' : 'dia'} em que o período termina`}
        </p>

        {isPeriod ? (
          <Calendar
            inline
            view="month"
            dateFormat="mm/yy"
            yearRange="1980:2030"
            value={value[active]}
            onChange={(e) => {
              if (e.value instanceof Date) pickDay(e.value);
            }}
          />
        ) : (
          <>
            <div className="date-jump">
              <span className="date-jump-label">Ir para</span>
              <button
                type="button"
                className={`date-jump-btn ${pane === 'months' ? 'is-open' : ''}`}
                onClick={() => setPane(pane === 'months' ? 'days' : 'months')}
              >
                {monthNames[viewDate.getMonth()]}
                <LuChevronDown />
              </button>
              <button
                type="button"
                className={`date-jump-btn ${pane === 'years' ? 'is-open' : ''}`}
                onClick={() => {
                  setDecade(Math.floor(viewDate.getFullYear() / 10) * 10);
                  setPane(pane === 'years' ? 'days' : 'years');
                }}
              >
                {viewDate.getFullYear()}
                <LuChevronDown />
              </button>
            </div>

            {pane === 'months' ? (
              <div className="date-picker-pane">
                <div className="date-pane-head">
                  <strong>{`Escolha o mês de ${viewDate.getFullYear()}`}</strong>
                </div>
                <div className="date-pane-grid cols-4">
                  {shortMonths.map((name, index) => (
                    <button
                      key={name}
                      type="button"
                      className={index === viewDate.getMonth() ? 'is-selected' : ''}
                      onClick={() => {
                        setViewDate(new Date(viewDate.getFullYear(), index, 1));
                        setPane('days');
                      }}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {pane === 'years' ? (
              <div className="date-picker-pane">
                <div className="date-pane-head">
                  <button
                    type="button"
                    className="date-pane-nav"
                    aria-label="Faixa anterior"
                    disabled={decade <= firstYear - (firstYear % 10)}
                    onClick={() => setDecade(decade - 10)}
                  >
                    <LuChevronLeft />
                  </button>
                  <strong>{`${decade} – ${decade + 9}`}</strong>
                  <button
                    type="button"
                    className="date-pane-nav"
                    aria-label="Próxima faixa"
                    disabled={decade + 10 > lastYear}
                    onClick={() => setDecade(decade + 10)}
                  >
                    <LuChevronRight />
                  </button>
                </div>
                <div className="date-pane-grid cols-5">
                  {Array.from({ length: 10 }, (_, index) => decade + index).map((year) => (
                    <button
                      key={year}
                      type="button"
                      disabled={year < firstYear || year > lastYear}
                      className={year === viewDate.getFullYear() ? 'is-selected' : ''}
                      onClick={() => {
                        setViewDate(new Date(year, viewDate.getMonth(), 1));
                        setPane('months');
                      }}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {pane === 'days' ? (
              <Calendar
                inline
                selectionMode="range"
                numberOfMonths={2}
                viewDate={viewDate}
                onViewDateChange={(e) => setViewDate(startOfMonth(e.value as Date))}
                value={value.start || value.end ? ([value.start, value.end] as unknown as Date[]) : null}
                onChange={() => undefined}
                onSelect={(e) => {
                  if (e.value instanceof Date) pickDay(e.value);
                }}
              />
            ) : null}
          </>
        )}
        <div className="date-panel-foot">
          <button type="button" onClick={() => pickDay(new Date(new Date().setHours(0, 0, 0, 0)))}>
            Hoje
          </button>
          <button
            type="button"
            onClick={() => {
              onChange({ ...value, start: null, end: null });
              panel.current?.hide();
            }}
          >
            Limpar
          </button>
        </div>
      </OverlayPanel>
    </div>
  );
};

export default DateFilter;
